import uniq from 'lodash/uniq';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import {
    useNetSuiteAttachmentsFieldSettings,
    useNetSuiteExpensesFieldSettings,
    useNetSuiteItemsFieldSettings,
    useNetSuitePurchaseOrderFieldSettings,
} from 'shared/hooks';

import { CustomFieldsState, NetSuiteExpenseLineExpanded, NetSuiteItemLineExpanded } from '../data/types';
import { validateCustomFields } from '../helpers';
import {
    checkExpenseLinesAreInvalid,
    checkExpensesAndItemLinesAreEmpty,
    checkItemLinesAreInvalid,
    isFieldMandatory,
    isMandatoryFieldEmpty,
    validateExpenseAndItemLines,
} from '../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { messages } from './NetSuitePurchaseOrderRequestForm.messages';
import { PurchaseOrderFields } from './NetSuitePurchaseOrderRequestForm.types';

export const getPurchaseOrderValidationErrors = (data: {
    fields: Partial<PurchaseOrderFields>;
    customFieldsValues: CustomFieldsState;
    customFieldsList: domain.Field[];
    workflowId: string;
    expenseLines: NetSuiteExpenseLineExpanded[];
    itemLines: NetSuiteItemLineExpanded[];
    totalAmount: number;
    taxAmount: number;
    isEmailToSupplierEnabled: boolean;
    emailToSupplierData: domain.EmailToSupplier | null;
    defaultRate?: number;
    fieldSettings: ReturnType<typeof useNetSuitePurchaseOrderFieldSettings>;
    expensesFieldSettings: ReturnType<typeof useNetSuiteExpensesFieldSettings>;
    itemsFieldSettings: ReturnType<typeof useNetSuiteItemsFieldSettings>;
    attachmentsFieldSettings: ReturnType<typeof useNetSuiteAttachmentsFieldSettings>;
    attachments: domain.RequestAttachment[];
    companyCurrency: string;
    requestCurrencyISOCode: string | null;
}): string[] => {
    const {
        fields,
        customFieldsValues,
        customFieldsList,
        workflowId,
        expenseLines,
        itemLines,
        totalAmount,
        taxAmount,
        isEmailToSupplierEnabled,
        emailToSupplierData,
        fieldSettings,
        expensesFieldSettings,
        itemsFieldSettings,
        attachmentsFieldSettings,
        attachments,
        defaultRate,
        companyCurrency,
        requestCurrencyISOCode,
    } = data;

    const errors: string[] = [];

    const areExpenseLinesInvalid = checkExpenseLinesAreInvalid(
        expenseLines,
        expensesFieldSettings,
        domain.IntegrationCode.NetSuitePO
    );

    const areItemLinesInvalid = checkItemLinesAreInvalid(
        itemLines,
        itemsFieldSettings,
        domain.IntegrationCode.NetSuitePO
    );

    const areExpensesAndItemsEmpty = checkExpensesAndItemLinesAreEmpty(expenseLines, itemLines);

    if (!fields.vendor || !fields.date || areExpenseLinesInvalid || areItemLinesInvalid || areExpensesAndItemsEmpty) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.currency, fieldSettings.currency)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.address, fieldSettings.billingAddress)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.dueDate, fieldSettings.dueDate)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.employee, fieldSettings.employee)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.memo, fieldSettings.memo)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.terms, fieldSettings.terms)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.vendorReference, fieldSettings.reference)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.department, fieldSettings.department)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.class, fieldSettings.class)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.location, fieldSettings.location)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.vatRegistrationNumber, fieldSettings.vatRegistration)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.incoterm, fieldSettings.incoterm)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isFieldMandatory(attachmentsFieldSettings.attachments) && attachments.length === 0) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.shipTo, fieldSettings.shipTo)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.shippingAddress, fieldSettings.shippingAddress)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.shipDate, fieldSettings.shipDate)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.shipMethod, fieldSettings.shipMethod)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.freeOnBoard, fieldSettings.fob)) {
        errors.push(messages.mandatoryFieldsError);
    }

    if (isMandatoryFieldEmpty(fields.trackingNumbers, fieldSettings.trackingNumbers)) {
        errors.push(messages.mandatoryFieldsError);
    }

    validateExpenseAndItemLines({ expenseLines, itemLines, customFieldsList, workflowId, errors });

    if (totalAmount > constants.netSuiteConstants.maxTotalAmount) {
        errors.push(messages.totalAmountExceedError({ maxValue: constants.netSuiteConstants.maxTotalAmount }));
    }

    if (totalAmount < 0) {
        errors.push(messages.totalAmountLessZeroError);
    }

    if (taxAmount > constants.netSuiteConstants.taxAmountMaxValue) {
        errors.push(messages.taxAmountExceedError({ maxValue: constants.netSuiteConstants.taxAmountMaxValue }));
    }

    if (
        requestCurrencyISOCode !== companyCurrency &&
        typeof defaultRate !== 'number' &&
        typeof fields.exchangeRate !== 'number'
    ) {
        errors.push(messages.exchangeRateError);
    }

    validateCustomFields({
        customFieldsValues: Object.values(customFieldsValues),
        customFieldsLevels: ['Header', 'Header And Lines'],
        customFieldsList,
        workflowId,
        errors,
    });

    if (isEmailToSupplierEnabled) {
        const isToFieldFilled = emailToSupplierData?.to && emailToSupplierData.to.length > 0;

        if (!isToFieldFilled || !emailToSupplierData?.subject.trim() || !emailToSupplierData?.body.trim()) {
            errors.push(messages.mandatoryFieldsError);
        }
    }

    return uniq(errors);
};
